/**
 * @file     liveAnalyticsService.ts
 * @purpose  Thin forwarding layer that sends events to GA4 (gtag) and the
 *           Relta first-party analytics SDK. PostHog removed in P8 of the
 *           relta-analytics sprint.
 */
import type { AnalyticsTrackInput, LegacyAnalyticsTrackInput, IAnalyticsService } from './analyticsTypes';
import { createNoopAnalyticsService } from './noopAnalyticsService';
import { reltaAnalytics } from '@/services/analytics/relta/client';
import { ga4Only } from '@/services/analytics/gtagDelivery';

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */

const gtag = (...args: Parameters<NonNullable<Window['gtag']>>) => {
  if (typeof window === 'undefined') return;
  // Admin workspace embeds consumer components (map, listing sheets); their
  // GA4 events are agent work, not product behavior. Keep them out of GA4.
  if (window.location.pathname.startsWith('/admin')) return;
  const scoped =
    args[0] === 'event'
      ? [args[0], args[1], ga4Only(args[2] as Record<string, unknown> | undefined)]
      : args;
  window.gtag?.(...scoped);
};

/**
 * Creates a live analytics service that delegates to the noop service for
 * state management but also forwards events to GA4 and the Relta SDK.
 */
export const createLiveAnalyticsService = (): IAnalyticsService => {
  const base = createNoopAnalyticsService();

  return {
    ...base,

    identify: (userId?: string, traits?: Record<string, unknown>) => {
      base.identify(userId, traits);
      if (userId) {
        // Second emitter of the same identity event. Routed through the deduped
        // helper so AuthProvider and this path cannot both file one.
        void import('@/services/analytics/authIdentityEvent').then(({ trackAuthIdentity }) => {
          trackAuthIdentity({
            id: userId,
            email: traits?.email && typeof traits.email === 'string' ? traits.email : null,
          });
        }).catch(() => { /* analytics never blocks identify */ });
      }
    },

    page: (name?: string, properties?: Record<string, unknown>) => {
      base.page(name, properties);
      gtag('event', 'page_view', {
        page_title: name,
        ...properties,
      });
      const path = (properties?.path ?? name) as string | undefined;
      if (path) reltaAnalytics.track('page.view', { path, title: name });
    },

    track: (event: LegacyAnalyticsTrackInput) => {
      base.track(event);
      gtag('event', event.name, event.properties);
    },

    trackEvent: (input: AnalyticsTrackInput) => {
      base.trackEvent(input);
      const props = {
        event_category: input.category,
        event_label: input.label,
        // `pagePath` here is a logical placement tag (e.g. 'neighbourhood_map_tile',
        // 'navbar'), NOT a URL. Sending it as GA4's `page_path` made those tags
        // show up as fake pages in the Pages report. Send it as a custom
        // `link_location` dimension; GA4 keeps the real page via page_location.
        ...(input.pagePath ? { link_location: input.pagePath } : {}),
        ...input.metadata,
      };
      gtag('event', input.eventName, props);
    },

    trackSectionView: (sectionId: string, metadata?: Record<string, unknown>) => {
      base.trackSectionView(sectionId, metadata);
      gtag('event', 'section_view', { section_id: sectionId, ...metadata });
    },
  };
};
